import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { SKILLS } from "@/lib/skills";

const EMPTY = { name: "", description: "", example: "", skill_sets: [], non_speaking_modification: "", video_url: "", level: "1", tags: "" };

export function AdminGames() {
  const [games, setGames] = useState([]);
  const [editing, setEditing] = useState(null); // {id?} + form fields
  const [open, setOpen] = useState(false);

  const load = () => api.get("/admin/games").then(({ data }) => setGames(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing({ ...EMPTY, id: null }); setOpen(true); };
  const openEdit = (g) => {
    setEditing({
      id: g.id, name: g.name || "", description: g.description || "", example: g.example || "",
      skill_sets: (g.skill_sets || []).filter((s) => SKILLS.includes(s)), non_speaking_modification: g.non_speaking_modification || "",
      video_url: g.video_url || "",
      level: String(g.level ?? "1"), tags: (g.tags || []).join(", "),
    });
    setOpen(true);
  };

  const save = async () => {
    const payload = {
      name: editing.name.trim(),
      description: editing.description,
      example: editing.example,
      skill_sets: SKILLS.filter((s) => editing.skill_sets.includes(s)),
      non_speaking_modification: editing.non_speaking_modification,
      video_url: editing.video_url.trim(),
      level: /^\d+$/.test(String(editing.level).trim()) ? parseInt(editing.level, 10) : String(editing.level).trim().toUpperCase(),
      tags: editing.tags.split(",").map((s) => s.trim()).filter(Boolean),
    };
    if (!payload.name) { toast.error("Name is required"); return; }
    try {
      if (editing.id) await api.put(`/admin/games/${editing.id}`, payload);
      else await api.post("/admin/games", payload);
      toast.success(editing.id ? "Game updated" : "Game created");
      setOpen(false);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const remove = async (g) => {
    if (!window.confirm(`Delete game "${g.name}"?`)) return;
    try {
      await api.delete(`/admin/games/${g.id}`);
      setGames((gs) => gs.filter((x) => x.id !== g.id));
      toast.success(`Deleted "${g.name}"`);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const field = (k, v) => setEditing((e) => ({ ...e, [k]: v }));
  const toggleSkill = (s) => setEditing((e) => ({
    ...e, skill_sets: e.skill_sets.includes(s) ? e.skill_sets.filter((x) => x !== s) : [...e.skill_sets, s],
  }));

  return (
    <div data-testid="admin-games">
      <div className="flex items-center justify-between mb-4">
        <p className="text-[#545454]">{games.length} games in the library.</p>
        <Button onClick={openNew} className="bg-[#920e12] hover:bg-[#550001] text-white" data-testid="add-game-btn"><Plus size={16} className="mr-1" /> Add game</Button>
      </div>
      <div className="overflow-x-auto bg-white rounded-2xl border border-[#dfd8da]">
        <table className="w-full text-left text-sm">
          <thead className="bg-[#f9f6f6] text-[#545454] uppercase text-xs tracking-wide">
            <tr><th className="p-3">Name</th><th className="p-3">Level</th><th className="p-3">Skills</th><th className="p-3">Non-speaking</th><th className="p-3">Video</th><th className="p-3">Actions</th></tr>
          </thead>
          <tbody>
            {games.map((g) => (
              <tr key={g.id} className="border-t border-[#dfd8da]" data-testid={`admin-game-${g.id}`}>
                <td className="p-3 font-bold text-[#1a1617]">{g.name}</td>
                <td className="p-3">{g.level}</td>
                <td className="p-3 text-[#545454] max-w-[16rem] truncate">{(g.skill_sets || []).join(", ")}</td>
                <td className="p-3 text-[#15803d]">{g.non_speaking_modification ? "✓" : "—"}</td>
                <td className="p-3 text-[#15803d]">{g.video_url ? "▶" : "—"}</td>
                <td className="p-3">
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => openEdit(g)} data-testid={`edit-game-${g.id}`} className="border-[#920e12] text-[#920e12]"><Pencil size={14} /></Button>
                    <Button size="sm" variant="outline" onClick={() => remove(g)} data-testid={`delete-game-${g.id}`} className="border-[#991b1b] text-[#991b1b]"><Trash2 size={14} /></Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="game-editor-dialog">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit game" : "New game"}</DialogTitle>
            <DialogDescription>Fill in the game details, including a non-speaking modification so every participant can take part.</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <Label htmlFor="g-name">Name</Label>
                  <input id="g-name" value={editing.name} onChange={(e) => field("name", e.target.value)} data-testid="game-name" className="mt-1 w-full h-10 px-3 rounded-lg border border-[#d6cecf]" />
                </div>
                <div>
                  <Label htmlFor="g-level">Level (1/2/3/A/Y)</Label>
                  <input id="g-level" value={editing.level} onChange={(e) => field("level", e.target.value)} data-testid="game-level" className="mt-1 w-full h-10 px-3 rounded-lg border border-[#d6cecf]" />
                </div>
              </div>
              <div>
                <Label htmlFor="g-desc">Description</Label>
                <textarea id="g-desc" rows={3} value={editing.description} onChange={(e) => field("description", e.target.value)} data-testid="game-description" className="mt-1 w-full px-3 py-2 rounded-lg border border-[#d6cecf]" />
              </div>
              <div>
                <Label htmlFor="g-ex">Example</Label>
                <textarea id="g-ex" rows={2} value={editing.example} onChange={(e) => field("example", e.target.value)} data-testid="game-example" className="mt-1 w-full px-3 py-2 rounded-lg border border-[#d6cecf]" />
              </div>
              <div>
                <Label htmlFor="g-ns">Non-speaking modification</Label>
                <textarea id="g-ns" rows={2} value={editing.non_speaking_modification} onChange={(e) => field("non_speaking_modification", e.target.value)} data-testid="game-nonspeaking" className="mt-1 w-full px-3 py-2 rounded-lg border border-[#d6cecf]" />
              </div>
              <div>
                <Label htmlFor="g-video">YouTube video link</Label>
                <input id="g-video" value={editing.video_url} onChange={(e) => field("video_url", e.target.value)} data-testid="game-video-url" placeholder="https://youtu.be/…" className="mt-1 w-full h-10 px-3 rounded-lg border border-[#d6cecf]" />
              </div>
              <fieldset data-testid="game-skills">
                <legend className="text-sm font-medium">Skills</legend>
                <div className="mt-1 grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                  {SKILLS.map((s) => (
                    <label key={s} className="flex items-center gap-2 text-sm text-[#1a1617] cursor-pointer">
                      <input type="checkbox" checked={editing.skill_sets.includes(s)} onChange={() => toggleSkill(s)}
                        data-testid={`game-skill-${s.toLowerCase().replace(/\s+/g, "-")}`} className="h-4 w-4 accent-[#920e12]" />
                      {s}
                    </label>
                  ))}
                </div>
              </fieldset>
              <div>
                <div>
                  <Label htmlFor="g-tags">Tags (comma-separated)</Label>
                  <input id="g-tags" value={editing.tags} onChange={(e) => field("tags", e.target.value)} data-testid="game-tags" className="mt-1 w-full h-10 px-3 rounded-lg border border-[#d6cecf]" />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="bg-[#920e12] text-white" data-testid="save-game-btn">Save game</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
